import { useState } from 'react'
import { Trash } from '@phosphor-icons/react'
import { useNetworkStatus } from '@/api'
import { useI18n, type MessageKey } from '@/i18n'
import { env } from '@/lib/env'
import { useDeviceInfo } from '@/offline'
import type { PackLot } from '@/offline/sitePack'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  IconButton,
  Input,
  IntegerInput,
  PickerSheet,
  useToast,
} from '@/ui'
import type { ProcessState } from '@/features/process/projection'
import { ProcessScreen } from '@/features/process/components/ProcessScreen'
import { ScanButton, YesNo } from '@/features/process/components/bits'
import { useBoth } from '@/features/process/components/useText'
import { dispatchToBuyer, type DispatchResponse } from '../api'
import { checkDispatchLot, lotCodeFromQr, pouchesLeft } from '../model/reconcile'

/** F7.8 — Dispatch released pouches to a buyer: one trip, its invoice, vehicle and pack lots. */
export function DispatchPage() {
  return <ProcessScreen title="pack.dispatch.title">{(state) => <Dispatch state={state} />}</ProcessScreen>
}

interface Item {
  lot: PackLot
  pouches: number | null
}

function Dispatch({ state }: { state: ProcessState }) {
  const { t } = useI18n()
  const toast = useToast()
  const both = useBoth()
  const { online } = useNetworkStatus()
  const info = useDeviceInfo()
  const demo = env.isDev && !!info.registration?.team.id.startsWith('dev-')
  const [buyerId, setBuyerId] = useState<string | null>(null)
  const [invoice, setInvoice] = useState('')
  const [vehicle, setVehicle] = useState('')
  const [driver, setDriver] = useState('')
  const [driverPhone, setDriverPhone] = useState('')
  const [clean, setClean] = useState<boolean | null>(null)
  const [items, setItems] = useState<Item[]>([])
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<DispatchResponse | null>(null)

  const lotByCode = (code: string) => [...state.packLots.values()].find((l) => l.code === code)
  const scanCheck = (raw: string): string | null => {
    const code = lotCodeFromQr(raw)
    if (!code) return both('pack.dispatch.notCarton')
    const lot = lotByCode(code)
    if (!lot) return both('pack.dispatch.unknownLot')
    if (items.some((i) => i.lot.id === lot.id)) return both('pack.dispatch.already')
    const r = checkDispatchLot(lot, null)
    return r ? both(`pack.dispatch.refusal.${r}` as MessageKey) : null
  }
  const itemErr = (i: Item) => {
    if (i.pouches === null || i.pouches <= 0) return null
    const r = checkDispatchLot(i.lot, i.pouches)
    return r ? t(`pack.dispatch.refusal.${r}` as MessageKey, { n: pouchesLeft(i.lot) }) : null
  }
  const phoneOk = driverPhone === '' || /^\d{10}$/.test(driverPhone)
  const ready =
    !!buyerId &&
    invoice.trim().length > 0 &&
    vehicle.trim().length > 0 &&
    clean === true &&
    phoneOk &&
    items.length > 0 &&
    items.every((i) => i.pouches !== null && i.pouches > 0 && !itemErr(i)) &&
    (online || demo)
  const total = items.reduce((a, i) => a + (i.pouches ?? 0), 0)

  const send = async () => {
    if (!ready || !buyerId) return
    setSaving(true)
    try {
      const res = await dispatchToBuyer({
        kind: 'DISPATCH',
        from_location_id: state.siteId,
        to_org_id: buyerId,
        challan_no: invoice.trim(),
        vehicle_no: vehicle.trim().toUpperCase(),
        driver_name: driver.trim() || null,
        driver_phone: driverPhone || null,
        vehicle_clean: true,
        items: items.map((i) => ({ pack_lot_code: i.lot.code, pouches: i.pouches! })),
      })
      setDone(res)
      toast.success(t('pack.dispatch.done', { code: res.code }))
    } catch (e) {
      toast.error(t('pack.dispatch.failed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <div className="flex flex-col gap-4">
        <Banner tone="success" title={t('pack.dispatch.doneTitle', { code: done.code })}>
          {t('pack.dispatch.doneBody', { n: total })}
        </Banner>
        <Button size="xl" fullWidth onClick={() => window.print()}>
          {t('pack.dispatch.printNote')}
        </Button>
        <Button
          variant="secondary"
          size="touch"
          onClick={() => {
            setDone(null)
            setItems([])
            setInvoice('')
            setVehicle('')
            setDriver('')
            setDriverPhone('')
            setClean(null)
          }}
        >
          {t('pack.dispatch.another')}
        </Button>
      </div>
    )

  return (
    <div className="flex flex-col gap-4">
      {!online && !demo && <Banner tone="warn">{t('pack.dispatch.needsNetwork')}</Banner>}
      <Field label={<BilingualLabel k="pack.dispatch.buyer" />} required>
        <PickerSheet
          title={<BilingualLabel k="pack.dispatch.buyer" />}
          options={state.pack.buyers.map((b) => ({ value: b.id, label: b.name, searchText: b.name }))}
          value={buyerId}
          onChange={setBuyerId}
        />
      </Field>
      <Field label={<BilingualLabel k="pack.dispatch.invoice" />} required>
        <Input size="touch" value={invoice} maxLength={30} onChange={(e) => setInvoice(e.target.value)} />
      </Field>
      <Field label={<BilingualLabel k="pack.dispatch.vehicle" />} required>
        <Input size="touch" value={vehicle} maxLength={20} autoCapitalize="characters" onChange={(e) => setVehicle(e.target.value)} />
      </Field>
      <Field label={t('pack.dispatch.driver')}>
        <Input size="touch" value={driver} maxLength={80} onChange={(e) => setDriver(e.target.value)} />
      </Field>
      <Field label={t('pack.dispatch.driverPhone')} error={phoneOk ? undefined : t('pack.dispatch.phoneBad')}>
        <Input size="touch" inputMode="numeric" value={driverPhone} onChange={(e) => setDriverPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} />
      </Field>
      <Field label={<BilingualLabel k="pack.dispatch.clean" />} required>
        <YesNo value={clean} onChange={setClean} />
      </Field>
      {clean === false && <Banner tone="error">{t('pack.dispatch.notClean')}</Banner>}

      <ScanButton
        continuous={false}
        variant="secondary"
        label={<BilingualLabel k="pack.dispatch.scan" />}
        validate={scanCheck}
        onScan={(raw) => {
          const code = lotCodeFromQr(raw)
          const lot = code ? lotByCode(code) : undefined
          if (lot && !scanCheck(raw)) setItems((xs) => [...xs, { lot, pouches: null }])
        }}
      />
      {items.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('pack.dispatch.lots', { n: items.length })} />
          <CardBody className="flex flex-col gap-3">
            {items.map((i) => (
              <div key={i.lot.id} className="flex flex-col gap-2 border-b border-rule/70 pb-3 last:border-b-0">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-mono text-[15px] font-bold">{i.lot.code}</div>
                    <div className="text-[12px] text-muted">{t('pack.pouchesLeft', { n: pouchesLeft(i.lot) })}</div>
                  </div>
                  <IconButton
                    size="touch"
                    label={t('process.remove')}
                    icon={<Trash />}
                    onClick={() => setItems((xs) => xs.filter((x) => x.lot.id !== i.lot.id))}
                  />
                </div>
                <Field label={<BilingualLabel k="pack.dispatch.pouches" />} error={itemErr(i) ?? undefined} required>
                  <IntegerInput
                    value={i.pouches}
                    onChange={(n) => setItems((xs) => xs.map((x) => (x.lot.id === i.lot.id ? { ...x, pouches: n } : x)))}
                  />
                </Field>
              </div>
            ))}
            <p className="text-right font-mono text-[15px] font-bold">{t('pack.dispatch.total', { n: total })}</p>
          </CardBody>
        </Card>
      )}
      <Button size="xl" fullWidth disabled={!ready} loading={saving} onClick={() => void send()}>
        <BilingualLabel k="pack.dispatch.button" />
      </Button>
    </div>
  )
}
